import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Params,
  Router,
  RouterStateSnapshot
} from '@angular/router';
import { APP_BRAND_PLP_ROUTE, APP_CATEGORY_ROUTE, APP_SEARCH_ROUTE } from '~shared/constants';
import { catchError, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { CategoriesService } from '~app/modules/generic/services/categories.service';
import { Category } from '~app/modules/generic/models';
import { BrandPLPService } from '~app/modules/brand-plp/services/brand-plp.service';
import { DEFAULT_LIMIT } from '~app/modules/generic/constants';
import { isPlatformServer } from '@angular/common';

const categoryQueryParams = 'f.category.0';
const newCategoryQueryParams = 'f.categories.0';
const searchQueryParams = 'f.top.phrase';
const maximumBrandPlpNavigationParamsNumber = 1;
const firstBrandQueryParams = 'f.options.brand.0';

@Injectable({
  providedIn: 'root',
})
export class CatalogGuard implements CanActivate {
  constructor(
    private router: Router,
    private categoriesService: CategoriesService,
    private brandPLPService: BrandPLPService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  public canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean> | Observable<boolean> {

    if (!route.params) {
      return of(true);
    }

    if (isPlatformServer(this.platformId)) {
      return of(true);
    }

    if (this.canLoadBeConsidered(route.queryParams)) {
      const defaultParameters = {
        queryParams: {
          offset: 0,
          limit: DEFAULT_LIMIT,
        }
      };
      return this.brandPLPService.getBrandSlugById(route.queryParams[firstBrandQueryParams]).pipe(
        switchMap((slug: string) => {
          if (!slug) {
            return of(true);
          }
          return this.router.navigate([`/${APP_BRAND_PLP_ROUTE}/${slug}`], defaultParameters);
        }),
        catchError(() => {
          return of(true);
        })
      );
    }

    const topPhrase = route.queryParams[searchQueryParams];
    if (topPhrase) {
      return this.handleTopPhraseParam(route, topPhrase);
    }

    let categoryIdKey = null;
    if (route.queryParams[categoryQueryParams]) {
      categoryIdKey = categoryQueryParams;
    } else if (route.queryParams[newCategoryQueryParams]) {
      categoryIdKey = newCategoryQueryParams;
    }

    if (categoryIdKey) {
      const category = route.queryParams[categoryIdKey];
      const updatedParams = this.removeKeyFromParams(route.queryParams, categoryIdKey);
      return this.getCategory(category)
        .pipe(switchMap((newCategory) => {
          return this.router.navigate([`/${APP_CATEGORY_ROUTE}/${newCategory.slug}`], {
            queryParamsHandling: 'merge',
            queryParams: {
              ...updatedParams
            }
          });
        }));
    }

    return of(true);
  }

  private canLoadBeConsidered(queryParams: Params): boolean {
    const routeContainsSingleBrandFilter = this.hasSingleBrandFilter(queryParams);
    const routeHasMaximumFilters = Object.keys(queryParams).length === maximumBrandPlpNavigationParamsNumber;
    const isNavigationReferrerEmpty = !document.referrer;

    return routeContainsSingleBrandFilter && routeHasMaximumFilters && !isNavigationReferrerEmpty;
  }

  private handleTopPhraseParam(route: ActivatedRouteSnapshot, topPhrase: string) {
    let params = this.removeKeyFromParams(route.queryParams, searchQueryParams);

    let categoryIdQueryParamKey = null;
    if (route.queryParams[categoryQueryParams]) {
      categoryIdQueryParamKey = categoryQueryParams;
    } else if (route.queryParams[newCategoryQueryParams]) {
      categoryIdQueryParamKey = newCategoryQueryParams;
    }

    if (categoryIdQueryParamKey) {
      const categoryId = route.queryParams[categoryIdQueryParamKey];
      params = this.removeKeyFromParams(params, categoryIdQueryParamKey);
      return this.getCategory(categoryId)
        .pipe(
          switchMap((newCategory) => {
            return this.router.navigate([`/${APP_SEARCH_ROUTE}`], {
              queryParamsHandling: 'merge',
              queryParams: {
                'q': topPhrase,
                'f.categories.0': newCategory.slug,
                ...params
              }
            });
          }));
    } else {
      return this.router.navigate([`/${APP_SEARCH_ROUTE}`], {
        queryParamsHandling: 'merge',
        queryParams: {
          'q': topPhrase,
          ...params
        }
      });
    }
  }

  private hasSingleBrandFilter(queryParams: Params): boolean {
    const secondBrandQueryParams = 'f.options.brand.1';
    if (!queryParams.hasOwnProperty(secondBrandQueryParams)) {
      return queryParams.hasOwnProperty(firstBrandQueryParams);
    }

    return false;
  }

  private getCategory(categoryId: string): Observable<Category> {
    return this.categoriesService.getCategoryByIdOrSlug(categoryId);
  }

  private removeKeyFromParams(params: Params, key: string): Params {
    const updatedParams = {};
    Object.keys(params).forEach(param => {
      if (param !== key) {
        updatedParams[param] = params[param];
      }
    });

    return updatedParams;
  }
}
